/**
 * @fileoverview Inline Photo Upload Component for Checklist Items
 * @module InlinePhotoUpload
 * 
 * Business Context:
 * - Minimal photo upload UI shown inline with checklist items
 * - Photos are tagged with the specific checklist item ID
 * - Designed to not disrupt checklist flow
 * 
 * Technical Context:
 * - Uses same S3 presigned URL flow as PhotoUploader
 * - Adds checklistItemId to uploads for categorization
 * - Ultra-compact design that blends seamlessly
 */

import React, { useState, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { Camera, X, Image, Loader2, Check, Plus } from 'lucide-react';
import { apiClient } from '../../api/client';
import EvidenceThumbnail from './EvidenceThumbnail';

interface EvidenceDocument {
  id: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  checklistItemId?: string | null;
  uploadedAt: string;
}

interface InlinePhotoUploadProps {
  findingId: string;
  checklistItemId: string;
  promptText: string;
  guidanceTips: string[];
  existingPhotos: EvidenceDocument[];
  onUploadComplete: (evidence: EvidenceDocument) => void;
  onDeletePhoto?: ((evidenceId: string) => void) | undefined;
}

interface UploadingFile {
  file: File;
  progress: number;
  status: 'uploading' | 'confirming' | 'complete' | 'error';
  error?: string | undefined;
  previewUrl?: string | undefined;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'image/heif', 'application/pdf'];

const InlinePhotoUpload: React.FC<InlinePhotoUploadProps> = ({
  findingId,
  checklistItemId,
  promptText,
  guidanceTips,
  existingPhotos,
  onUploadComplete,
  onDeletePhoto
}) => {
  const [uploadingFiles, setUploadingFiles] = useState<Map<string, UploadingFile>>(new Map());
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): string | null => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return 'Please upload JPEG, PNG, or PDF files only.';
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'File too large (max 5MB).';
    }
    return null;
  };

  const createPreview = (file: File): string | undefined => {
    if (file.type.startsWith('image/')) {
      return URL.createObjectURL(file);
    }
    return undefined;
  };

  const uploadFile = async (file: File) => {
    const fileId = `${file.name}-${Date.now()}`;
    const previewUrl = createPreview(file);
    
    setUploadingFiles(prev => new Map(prev).set(fileId, {
      file,
      progress: 0,
      status: 'uploading',
      previewUrl
    }));

    try {
      const urlResponse = await apiClient.evidence.getUploadUrl({
        findingId,
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size
      });

      if (!urlResponse.data.success) {
        throw new Error(urlResponse.data.error?.message || 'Failed to get upload URL');
      }

      const { uploadUrl, s3Key } = urlResponse.data.data;

      setUploadingFiles(prev => {
        const updated = new Map(prev);
        const current = updated.get(fileId);
        if (current) updated.set(fileId, { ...current, progress: 40 });
        return updated;
      });

      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type }
      });

      if (!uploadResponse.ok) throw new Error('Upload failed');

      setUploadingFiles(prev => {
        const updated = new Map(prev);
        const current = updated.get(fileId);
        if (current) updated.set(fileId, { ...current, progress: 80, status: 'confirming' });
        return updated;
      });

      const confirmResponse = await apiClient.evidence.confirmUpload({
        findingId,
        s3Key,
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        checklistItemId
      });

      if (!confirmResponse.data.success) {
        throw new Error(confirmResponse.data.error?.message || 'Failed to confirm');
      }

      setUploadingFiles(prev => {
        const updated = new Map(prev);
        const current = updated.get(fileId);
        if (current) updated.set(fileId, { ...current, progress: 100, status: 'complete' });
        return updated;
      });

      onUploadComplete(confirmResponse.data.data);

      // Clean up preview URL and remove from list
      setTimeout(() => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setUploadingFiles(prev => {
          const updated = new Map(prev);
          updated.delete(fileId);
          return updated;
        });
      }, 1000);

    } catch (error: any) {
      console.error('Upload error:', error);
      setUploadingFiles(prev => {
        const updated = new Map(prev);
        const current = updated.get(fileId);
        if (current) {
          updated.set(fileId, { ...current, status: 'error', error: error.message || 'Upload failed' });
        }
        return updated;
      });
    }
  };

  const handleFiles = useCallback((files: FileList | null) => {
    if (!files) return;
    Array.from(files).forEach(file => {
      const error = validateFile(file);
      if (error) {
        toast.error(error);
        return;
      }
      uploadFile(file);
    });
  }, [findingId, checklistItemId]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
    e.target.value = '';
  }, [handleFiles]);

  const handleDeletePhoto = async (evidenceId: string) => {
    if (!onDeletePhoto) return;
    if (confirm('Delete this photo?')) {
      try {
        await apiClient.evidence.delete(evidenceId);
        onDeletePhoto(evidenceId);
      } catch (error) {
        console.error('Failed to delete photo:', error);
      }
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const hasPhotos = existingPhotos.length > 0 || uploadingFiles.size > 0;

  return (
    <div className="mt-2 ml-6 pl-3">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={ALLOWED_TYPES.join(',')}
        onChange={handleChange}
        className="sr-only"
      />

      {/* Photo thumbnails row */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* Existing photos as thumbnails */}
        {existingPhotos.map((photo) => (
          <div
            key={photo.id}
            className="relative group w-12 h-12 rounded-lg overflow-hidden bg-gray-100 border border-gray-200"
            title={photo.fileName}
          >
            {photo.fileType.startsWith('image/') ? (
              <EvidenceThumbnail
                evidenceId={photo.id}
                fileName={photo.fileName}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Image className="w-5 h-5 text-gray-400" />
              </div>
            )}
            {/* Delete button on hover */}
            {onDeletePhoto && (
              <button
                onClick={() => handleDeletePhoto(photo.id)}
                className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
              >
                <X className="w-3 h-3" />
              </button>
            )}
            {/* Success checkmark */}
            <div className="absolute bottom-0.5 right-0.5 w-4 h-4 bg-green-500 text-white rounded-full flex items-center justify-center">
              <Check className="w-2.5 h-2.5" />
            </div>
          </div>
        ))}

        {/* Uploading photos */}
        {Array.from(uploadingFiles.entries()).map(([fileId, upload]) => (
          <div
            key={fileId}
            className="relative w-12 h-12 rounded-lg overflow-hidden bg-gray-100 border border-gray-200"
          >
            {upload.previewUrl ? (
              <img
                src={upload.previewUrl}
                alt="Uploading"
                className="w-full h-full object-cover opacity-60"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Image className="w-5 h-5 text-gray-400" />
              </div>
            )}
            {/* Upload overlay */}
            <div className="absolute inset-0 flex items-center justify-center bg-black/20">
              {upload.status === 'error' ? (
                <X className="w-5 h-5 text-red-500" />
              ) : upload.status === 'complete' ? (
                <Check className="w-5 h-5 text-green-500" />
              ) : (
                <Loader2 className="w-5 h-5 text-white animate-spin" />
              )}
            </div>
            {/* Progress bar at bottom */}
            {upload.status !== 'complete' && upload.status !== 'error' && (
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-300">
                <div
                  className="h-full bg-blue-500 transition-all duration-300"
                  style={{ width: `${upload.progress}%` }}
                />
              </div>
            )}
          </div>
        ))}

        {/* Add photo button - always visible, compact */}
        <button
          onClick={triggerFileInput}
          className={`
            flex items-center justify-center rounded-lg border-2 border-dashed transition-all
            ${hasPhotos 
              ? 'w-12 h-12 border-gray-300 hover:border-blue-400 hover:bg-blue-50' 
              : 'gap-2 px-3 py-2 border-gray-300 hover:border-blue-400 hover:bg-blue-50'
            }
          `}
          title="Add photo"
        >
          {hasPhotos ? (
            <Plus className="w-5 h-5 text-gray-400" />
          ) : (
            <>
              <Camera className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-600">Add photo</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default InlinePhotoUpload;
